<template>
	<SimulatorSheet :stepNum="stepNum" :stepName="stepName">
		<div class="simulator__grid simulator__grid--1-2 mb-60 align-items-center">
			<p class="sheet__controlTitle">{{ $t("Deformação") }}</p>
			<div class="grid--4">
				<BFieldBlock :label="$t('Deformação')">
					<BFieldNumber
						v-model="values.deformation"
						prefix="L/"
						:precision="0"
						:min="0"
					/>
				</BFieldBlock>
			</div>
		</div>

		<div class="simulator__grid simulator__grid--1-2 mb-60">
			<p class="sheet__controlTitle">{{ $t("Combinação") }}</p>
			<div
				:style="{
					display: 'flex',
					'flex-direction': 'column',
					gap: '20px',
				}"
			>
				<BRadio
					v-for="option in simulationOptions.combination.options"
					:key="option.id"
					v-model="values.combination"
					:label="$t(option.label)"
					:optionValue="option.id"
				/>
			</div>
		</div>

		<div class="sheet__divider" v-if="isAxialLoadUsed"></div>

		<div class="simulator__grid simulator__grid--1-2 mb-40" v-if="isAxialLoadUsed">
			<div></div>
			<div>
				<!-- Axial loads -->
				<button
					class="sheet__bigAddBtn sheet__bigAddBtn--dark"
					@click="() => (values.axialLoads.use = true)"
					v-if="!values.axialLoads.use"
				>
					{{ $t("Esforço axial") }} ({{ $t("opcional") }})
					<BIcon name="AddAlt" size="16"></BIcon>
				</button>

				<div class="additionalFieldsCard" v-else>
					<div class="flex align-items-center justify-between mb-30">
						<p class="fsz-l color-neutral-900">
							{{ $t("Esforço axial") }} ({{ $t("opcional") }})
						</p>

						<div class="flex">
							<BBtn
								size="m"
								variant="text"
								shape="circle"
								color="secondary"
								@click="handleRefreshAxialLoadsBtnClick"
							>
								<BIcon name="Reset" size="20px" />
							</BBtn>
							<BBtn
								size="m"
								variant="text"
								shape="circle"
								color="secondary"
								@click="handleDeleteAxialLoadsBtnClick"
							>
								<BIcon name="Delete" size="20px" />
							</BBtn>
						</div>
					</div>

					<div>
						<p class="fsz-l color-neutral-600 mb-20">
							{{ $t("Tipo de carga") }}
						</p>

						<div
							class="mb-60"
							:style="{
								display: 'flex',
								'flex-direction': 'column',
								gap: '20px',
							}"
						>
							<BRadio
								v-for="option in simulationOptions.axialLoadTypes.options"
								:key="option.id"
								v-model="values.axialLoads.type"
								:label="$t(option.label)"
								:optionValue="option.id"
							/>
						</div>

						<div class="mb-60 grid--4">
							<BFieldBlock :label="$t('Carga')">
								<BFieldNumber
									v-model="values.axialLoads.value"
									:min="0"
									:precision="2"
									:step="0.01"
									suffix="kN"
								/>
							</BFieldBlock>
						</div>

						<div
							:style="{
								display: 'flex',
								'flex-direction': 'column',
								gap: '20px',
							}"
						>
							<BRadio
								v-for="option in simulationOptions.direction.options"
								:key="option.id"
								v-model="values.axialLoads.direction"
								:label="$t(option.label)"
								:optionValue="option.id"
							/>
						</div>
					</div>
				</div>
			</div>
		</div>

		<div class="sheet__divider"></div>

		<div class="mb-40">
			<div
				class="simulator__grid simulator__grid--1-2"
				v-if="!values.locks.use"
			>
				<div></div>
				<!-- Locks -->
				<div>
					<button
						class="sheet__bigAddBtn sheet__bigAddBtn--dark"
						@click="() => (values.locks.use = true)"
					>
						{{ $t("Travamentos") }} ({{ $t("opcional") }})
						<BIcon name="AddAlt" size="16"></BIcon>
					</button>
				</div>
			</div>

			<div
				v-else
				class="simulator__grid"
				:class="
					isLocksCardLarge ? 'simulator__grid--1' : 'simulator__grid--1-2'
				"
			>
				<div v-if="!isLocksCardLarge"></div>
				<!-- Locks -->
				<div>
					<div
						class="additionalFieldsCard"
						:class="{
							'is-large': isLocksCardLarge,
						}"
					>
						<div
							class="simulator__grid"
							:class="
								isLocksCardLarge ? 'simulator__grid--1-2' : 'simulator__grid--1'
							"
						>
							<div v-if="isLocksCardLarge"></div>

							<div class="additionalFieldsCard__constrained">
								<div class="flex align-items-center justify-between mb-30">
									<p class="fsz-l color-neutral-900">
										{{ $t("Travamentos") }} ({{ $t("opcional") }})
									</p>

									<div class="flex">
										<BBtn
											size="m"
											variant="text"
											shape="circle"
											color="secondary"
											@click="handleRefreshLocksBtnClick"
										>
											<BIcon name="Reset" size="20px" />
										</BBtn>
										<BBtn
											size="m"
											variant="text"
											shape="circle"
											color="secondary"
											@click="handleDeleteLocksBtnClick"
										>
											<BIcon name="Delete" size="20px" />
										</BBtn>
									</div>
								</div>
							</div>
						</div>

						<!-- upper fibre -->
						<div class="mb-60">
							<div
								class="simulator__grid"
								:class="
									isLocksCardLarge
										? 'simulator__grid--1-2'
										: 'simulator__grid--1'
								"
							>
								<div v-if="isLocksCardLarge"></div>
								<div class="additionalFieldsCard__constrained">
									<p class="fsz-l color-neutral-600 mb-20">
										{{ $t("Fibra Superior") }}
									</p>

									<div
										:style="{
											display: 'flex',
											'flex-direction': 'column',
											gap: '20px',
										}"
									>
										<BRadio
											v-for="option in simulationOptions.upperFibre.options"
											:key="option.id"
											v-model="values.locks.upperFibre.type"
											:label="$t(option.label)"
											:optionValue="option.id"
										/>
									</div>

									<div class="mt-40" v-if="values.locks.upperFibre.type == 1">
										<div class="mb-40">
											<BToggle
												:label="$t('Valor por vão')"
												v-model="
													values.locks.upperFibre.lateralLocks.cfgMode
														.isSpanBySpan
												"
											/>
										</div>

										<div
											v-if="
												!values.locks.upperFibre.lateralLocks.cfgMode
													.isSpanBySpan
											"
										>
											<div class="grid--4">
												<BFieldBlock :label="$t('Valor')">
													<BFieldNumber
														:min="0"
														:precision="0"
														:step="1"
														v-model="
															values.locks.upperFibre.lateralLocks[
																sampleSpanType
															][0]
														"
													/>
												</BFieldBlock>
											</div>
										</div>
									</div>
								</div>
							</div>

							<SimulatorSpanMappedValuesEditor
								v-if="
									values.locks.upperFibre.type == 1 &&
										values.locks.upperFibre.lateralLocks.cfgMode.isSpanBySpan
								"
								:includeConsoles="false"
								:values="values.locks.upperFibre.lateralLocks"
								:fieldProps="{
									min: 0,
									precision: 0,
									step: 1,
								}"
							/>
						</div>

						<!-- lower fibre -->
						<div>
							<div
								class="simulator__grid"
								:class="
									isLocksCardLarge
										? 'simulator__grid--1-2'
										: 'simulator__grid--1'
								"
							>
								<div v-if="isLocksCardLarge"></div>
								<div class="additionalFieldsCard__constrained">
									<p class="fsz-l color-neutral-600 mb-20">
										{{ $t("Fibra Inferior") }}
									</p>

									<BCheckbox
										v-model="values.locks.lowerFibre.use"
										:label="$t('Número de travamentos laterais')"
									/>

									<div class="mt-40" v-if="values.locks.lowerFibre.use">
										<div class="mb-40">
											<BToggle
												:label="$t('Valor por vão')"
												v-model="
													values.locks.lowerFibre.lateralLocks.cfgMode
														.isSpanBySpan
												"
											/>
										</div>

										<div
											v-if="
												!values.locks.lowerFibre.lateralLocks.cfgMode
													.isSpanBySpan
											"
										>
											<div class="grid--4">
												<BFieldBlock :label="$t('Valor')">
													<BFieldNumber
														:min="0"
														:precision="0"
														:step="1"
														v-model="
															values.locks.lowerFibre.lateralLocks[
																sampleSpanType
															][0]
														"
													/>
												</BFieldBlock>
											</div>
										</div>
									</div>
								</div>
							</div>

							<SimulatorSpanMappedValuesEditor
								v-if="values.locks.lowerFibre.lateralLocks.cfgMode.isSpanBySpan"
								:includeConsoles="false"
								:values="values.locks.lowerFibre.lateralLocks"
								:fieldProps="{
									min: 0,
									precision: 0,
									step: 1,
								}"
							/>
						</div>
					</div>
				</div>
			</div>
		</div>

		<div class="simulator__grid simulator__grid--1-2">
			<div></div>
			<div>
				<!-- Locks -->
				<div>
					<button
						class="sheet__bigAddBtn sheet__bigAddBtn--dark"
						@click="() => (values.claddingProperties.use = true)"
						v-if="!values.claddingProperties.use"
					>
						{{ $t("Contribuição da chapa de revestimento") }} ({{
							$t("opcional")
						}})
						<BIcon name="AddAlt" size="16"></BIcon>
					</button>

					<div class="additionalFieldsCard" v-else>
						<div class="flex align-items-center justify-between mb-30">
							<p class="fsz-l color-neutral-900">
								{{ $t("Contribuição da chapa de revestimento") }} ({{
									$t("opcional")
								}})
							</p>

							<div class="flex">
								<BBtn
									size="m"
									variant="text"
									shape="circle"
									color="secondary"
									@click="handleRefreshCladdingPropertiesBtnClick"
								>
									<BIcon name="Reset" size="20px" />
								</BBtn>
								<BBtn
									size="m"
									variant="text"
									shape="circle"
									color="secondary"
									@click="handleDeleteCladdingPropertiesBtnClick"
								>
									<BIcon name="Delete" size="20px" />
								</BBtn>
							</div>
						</div>

						<div>
							<div class="grid--4 align-items-end">
								<BFieldBlock :label="$t('Número de fixações')">
									<BFieldNumber
										v-model="values.claddingProperties.coveringInertia"
										:min="0"
										:step="1"
										:precision="0"
									/>
								</BFieldBlock>
								<BFieldBlock
									:label="$t('Inércia de Chapa de Revestimento (m4/m)')"
								>
									<BFieldNumber
										v-model="values.claddingProperties.fixationsPerMeter"
										:min="0"
									/>
								</BFieldBlock>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<div class="sheet__footer">
			<BBtn
				size="l"
				variant="text"
				@click="handleRestartBtnClick"
				:disabled="isSaving"
				>{{ $t("Reiniciar") }}</BBtn
			>
			<BBtn size="l" @click="handleSaveBtnClick" :disabled="isSaving">{{
				$t("Salvar e Calcular")
			}}</BBtn>
		</div>
	</SimulatorSheet>
</template>

<script>
import { mapMutations, mapGetters, mapActions, mapState } from "vuex";
import { deepClone, excludeKeys, wait } from "@/utils";

import SimulatorSheet from "./SimulatorSheet.vue";
import SimulatorSpanMappedValuesEditor from "./SimulatorSpanMappedValuesEditor.vue";

import BSelector from "@/components/base/BSelector.vue";
import BSelect from "@/components/base/BSelect.vue";
import BFieldBlock from "@/components/base/BFieldBlock.vue";
import BCheckbox from "@/components/base/BCheckbox.vue";
import BFieldNumber from "@/components/base/BFieldNumber.vue";
import BBtn from "@/components/base/BBtn.vue";
import BRadio from "@/components/base/BRadio.vue";
import BToggle from "@/components/base/BToggle.vue";
import BIcon from "@/components/base/BIcon/index.vue";

import {
	getDefaultLocks,
	getDefaultCladdingProperties,
	getDefaultAxialLoads,
} from "@/store/modules/simulations/models/simulationDefaults";

import mixinObserveChanges from "@/pages/Simulator/mixins/observeChanges";
import mixinEmitLifecycleEvents from "@/mixins/emitLifecycleEvents";

// axial loads are not part of the calculation at the moment
const isAxialLoadUsed = false;

export default {
	components: {
		BSelector,
		BToggle,
		BFieldBlock,
		BCheckbox,
		BSelect,
		BFieldNumber,
		BBtn,
		BRadio,
		BIcon,
		SimulatorSpanMappedValuesEditor,
		SimulatorSheet,
	},

	mixins: [mixinEmitLifecycleEvents, mixinObserveChanges],

	props: {
		...SimulatorSheet.props,
	},

	data() {
		return {
			values: null,
			isSaving: false,
			isAxialLoadUsed,
		};
	},
	computed: {
		...mapState("simulator", {
			simulationOptions: "simulationOptions",
			simulationDefaults: "simulationDefaults",
			storeSimulation: "simulation",
		}),

		storeValues() {
			return this.storeSimulation.values;
		},

		isLocksCardLarge() {
			return (
				(this.values.locks.upperFibre.type == 1 &&
					this.values.locks.upperFibre.lateralLocks.cfgMode.isSpanBySpan) ||
				this.values.locks.lowerFibre.lateralLocks.cfgMode.isSpanBySpan
			);
		},
	},
	methods: {
		...mapActions("simulator", ["save"]),
		...mapMutations("simulator", ["setSimulationValues"]),

		handleSaveBtnClick() {
			this.$emit("save", this.values, true);
		},

		handleRestartBtnClick() {
			this.$emit("restart");
		},

		handleRefreshLocksBtnClick() {
			this.values.locks = {
				...getDefaultLocks({ spans: this.values.spans }),
				use: true,
			};
		},
		handleRefreshAxialLoadsBtnClick() {
			this.values.axialLoads = {
				...getDefaultAxialLoads(),
				use: true,
			};
		},

		handleDeleteLocksBtnClick() {
			this.values.locks.use = false;
		},

		handleDeleteAxialLoadsBtnClick() {
			this.values.axialLoads.use = false;
		},

		handleRefreshCladdingPropertiesBtnClick() {
			this.values.claddingProperties = {
				...getDefaultCladdingProperties(),
				use: true,
			};
		},
		handleDeleteCladdingPropertiesBtnClick() {
			this.values.claddingProperties.use = false;
		},
	},

	watch: {
		"values.axialLoads.use": {
			handler(use) {
				if (!use) {
					this.values.axialLoads = {
						...getDefaultAxialLoads(),
						use: false,
					};
				}
			},
		},

		"values.claddingProperties.use": {
			handler(use) {
				if (!use) {
					this.values.claddingProperties = {
						...getDefaultCladdingProperties(),
						use: false,
					};
				}
			},
		},

		"values.locks.use": {
			handler(use) {
				if (!use) {
					this.values.locks = {
						...getDefaultLocks({ spans: this.values.spans }),
						use: false,
					};
				}
			},
		},
		"values.locks.upperFibre.type": {
			handler(type) {
				if (type == 0) {
					this.values.locks.upperFibre = {
						...getDefaultLocks({ spans: this.values.spans }).upperFibre,
						type: 0,
					};
				}
			},
		},
		"values.locks.lowerFibre.use": {
			handler(use) {
				if (!use) {
					this.values.locks.lowerFibre = {
						...getDefaultLocks({ spans: this.values.spans }).lowerFibre,
						use: false,
					};
				}
			},
		},

		"values.locks.upperFibre.lateralLocks": {
			handler(val) {
				if (!val.cfgMode.isSpanBySpan) {
					const sampleItem = val[this.sampleSpanType][0];

					["main", "console"].forEach((spanType) => {
						val[spanType].forEach((item, idx) => {
							val[spanType][idx] = sampleItem;
						});
					});
				}
			},
			deep: true,
		},

		"values.locks.lowerFibre.lateralLocks": {
			handler(val) {
				if (!val.cfgMode.isSpanBySpan) {
					const sampleItem = val[this.sampleSpanType][0];

					["main", "console"].forEach((spanType) => {
						val[spanType].forEach((item, idx) => {
							val[spanType][idx] = sampleItem;
						});
					});
				}
			},
			deep: true,
		},
	},

	created() {
		this.values = deepClone(this.storeValues);
		this.sampleSpanType = this.values.spans.useConsole ? "console" : "main";
	},
};
</script>

<style lang="scss"></style>
